import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { ActivityIndicator, FlatList, ScrollView, Text, TouchableOpacity, View } from "react-native"
import { useNavigation, useRoute } from "@react-navigation/native"
import api from "../api-client"
import { ThemeContext } from "../utility/context/ThemeContext"
import { ShadowView, textStyles } from "../style"
import { MAX_CONTENT_WIDTH } from "../config"
import type { Company, SelectedIcon as ISelectedIcon } from "@ecountabl/lib"
import Haptics from "../utility/haptics"
import { useAppSelector } from "../store"
import { MaterialIcons } from "@expo/vector-icons"
import { SkeletonBlock, SkeletonGroup, SkeletonLine } from "../utility/skeleton"
import { ValueBadge } from "../utility/value-badge"
import Logo from "../utility/logo"
import Analytics from "../utility/analytics"
import impressionAggregator from "../utility/impression-aggregator"
import AutoShrinkText from "../utility/AutoShrinkText"

type SearchRouteParams = {
  term?: string;
  values?: string | string[];
  sort?: string;
}

const normalizeParamArray = (param?: string | string[]): string[] => {
  if (!param) return []
  return (Array.isArray(param) ? param : [param])
    .map(v => `${v}`.trim())
    .filter(Boolean)
}

const companySortLabelMap:Record<string, string> = {
  relevance: "Relevance",
  name_asc: "Name A to Z",
  name_desc: "Name Z to A",
}
const PAGE_SIZE = 20

const CompanySearchResults:React.FC = () => {
  const navigation = useNavigation<any>()
  const route = useRoute() as unknown as { params?: SearchRouteParams }
  const { theme } = useContext(ThemeContext)
  const { bgMain, borderColor, textMain, primary, bgSecond } = theme.colors
  const { map: valuesMap } = useAppSelector(state => state.values)

  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<Company[]>([])
  const [page, setPage] = useState(1)
  const [canFetchMore, setCanFetchMore] = useState(true)
  const latestRequestRef = useRef<number>(0)

  const params = route?.params || {}
  const values = normalizeParamArray(params.values)

  const getValueBubbleLabel = useCallback((valueSlugOrMode: string): string => {
    if (valueSlugOrMode === "any") return "Any"
    if (valueSlugOrMode === "none") return "None"
    return valuesMap?.[valueSlugOrMode]?.label || valueSlugOrMode
  }, [valuesMap])

  const getValueBubbleBgColor = useCallback((valueSlugOrMode: string): string | undefined => {
    if (valueSlugOrMode === "any" || valueSlugOrMode === "none") {
      return undefined
    }
    const valueRecord = (
      valuesMap?.[valueSlugOrMode] as unknown as Record<string, unknown> | undefined
    )
    const valueColor = (
      typeof valueRecord?.color === "string"
        ? valueRecord.color
        : typeof valueRecord?.hexColor === "string"
          ? valueRecord.hexColor
          : undefined
    )
    return valueColor ? `${valueColor}40` : undefined
  }, [valuesMap])

  const getSortBubbleLabel = useCallback((sortKey?: string): string => {
    if (!sortKey) return ""
    return companySortLabelMap[sortKey] || sortKey
  }, [])

  const fetchCompanies = useCallback(async (nextPage: number, append: boolean) => {
    // increment the request id to avoid race conditions
    const requestId = ++latestRequestRef.current
    
    const rawTerm = typeof params.term === "string" ? params.term : ""
    const term = rawTerm.trim()

    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
      setError(null)
      setResults([])
    }

    try {
      const companies = await api.companySearch({
        term: term || undefined,
        sort: params.sort,
        page: nextPage,
        limit: PAGE_SIZE,
      })

      if (requestId !== latestRequestRef.current) {
        return
      }

      const nextResults = companies || []
      setResults(prev => (append ? [...prev, ...nextResults] : nextResults))
      setPage(nextPage)
      setCanFetchMore(nextResults.length === PAGE_SIZE)
    } catch (ex) {
      if (requestId !== latestRequestRef.current) {
        return
      }

      if (!append) {
        setError(ex?.message || "There was an error while searching companies.")
      } else {
        setCanFetchMore(false)
      }
    } finally {
      if (requestId !== latestRequestRef.current) {
        return
      }

      if (append) {
        setLoadingMore(false)
      } else {
        setLoading(false)
      }
    }
  }, [params.sort, params.term, params.values])

  useEffect(() => {
    setPage(1)
    setCanFetchMore(true)
    fetchCompanies(1, false)
  }, [fetchCompanies])

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !canFetchMore) return
    fetchCompanies(page + 1, true)
  }, [canFetchMore, fetchCompanies, loading, loadingMore, page])

  const bubbles = useMemo(() => {
    const ret:{label: string; onClear?: () => void; bgColor?: string}[] = []
    if (params.term) {
      ret.push({
        label: `Search: ${params.term}`
      })
    }
    if (values.length > 0) {
      values.forEach((value) => {
        ret.push({
          label: `Value: ${getValueBubbleLabel(value)}`,
          bgColor: getValueBubbleBgColor(value),
          onClear: () => {
            const nextValues = values.filter(v => v !== value)
            navigation.setParams({ values: nextValues.length > 0 ? nextValues : undefined })
          }
        })
      })
    }
    if (params.sort) {
      ret.push({
        label: `Sort: ${getSortBubbleLabel(params.sort)}`,
        onClear: () => navigation.setParams({ sort: undefined })
      })
    }
    return ret
  }, [
    getSortBubbleLabel,
    getValueBubbleBgColor,
    getValueBubbleLabel,
    navigation,
    params.sort,
    params.term,
    values
  ])

  const viewabilityConfig = {
    itemVisiblePercentThreshold: 25,
  }

  const onViewableItemsChanged = useCallback((
    { viewableItems }: { viewableItems: Array<{ item: Company; index: number | null }> }
  ) => {
    viewableItems.forEach((viewable) => {
      const company = viewable.item as Company | undefined
      if (!company?._id) return
      impressionAggregator.company.add({
        companyId: company._id.toString(),
        companyName: company.name,
        index: viewable.index ?? 0,
        source: "company-search-results",
      })
    })
  }, [])

  const renderValueBadges = (company: Company) => {
    if (!company?.valuesAligned || company.valuesAligned.length === 0) {
      return (
        <Text style={[textStyles.subscript, {color: textMain}]}>
          Not Values Aligned
        </Text>
      )
    }

    return (
      <View style={{flexDirection: "row", flexWrap: "wrap", gap: 4}}>
        {company.valuesAligned.map((value: unknown, idx: number) => {
          const rec = (value || {}) as Record<string, unknown>
          const color = typeof rec.color === "string" ? rec.color : undefined
          const icon = rec.icon as unknown
          const slug = typeof rec.slug === "string" ? rec.slug : undefined
          const label = typeof rec.label === "string" ? rec.label : undefined
          return (
            <ValueBadge
              key={slug || label || `${company._id}-${idx}`}
              color={typeof color === "string" ? color : undefined}
              accentFallbackColor={primary}
              icon={(icon as ISelectedIcon) ?? null}
              label={typeof label === "string" ? label : undefined}
              size={22}
              iconSize={14}
            />
          )
        })}
      </View>
    )
  }

  const renderSkeletonUI = () => {
    return (
      <ScrollView
        style={{flex: 1}}
        contentContainerStyle={{
          width: "100%",
          maxWidth: MAX_CONTENT_WIDTH,
          alignSelf: "center",
          paddingHorizontal: 16,
          gap: 10,
          paddingVertical: 16
        }}
      >
        <SkeletonGroup>
          {Array.from({ length: 6 }).map((_, index) => (
            <View
              key={`company-search-skeleton-${index}`}
              style={{
                borderRadius: 12,
                padding: 12,
                backgroundColor: bgSecond,
                flexDirection: "row",
                gap: 12,
                alignItems: "center",
              }}
            >
              <SkeletonBlock
                style={{ width: 44, height: 44 }}
                radius={8}
              />
              <View style={{flex: 1, gap: 6}}>
                <SkeletonLine width="65%" height={16} radius={6} />
                <SkeletonLine width="92%" height={22} radius={6} />
                <SkeletonLine width="48%" height={12} radius={6} />
              </View>
            </View>
          ))}
        </SkeletonGroup>
      </ScrollView>
    )
  }

  const renderNoResultsUI = () => {
    return (
      <View style={{flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: bgMain, padding: 16, gap: 8}}>
        <View style={{alignSelf: "center", width: 100, aspectRatio: 1, backgroundColor: `${primary}4D`, borderRadius: 100, justifyContent: "center", alignItems: "center"}}>
          <MaterialIcons name="search-off" size={48} color={textMain}/>
        </View>
        <View>
          <Text style={[textStyles.body, {color: textMain, textAlign: "center"}]}>
            Uh oh, no companies found.
          </Text>
          <Text style={[textStyles.body, {color: textMain, textAlign: "center"}]}>
            Try adjusting your search filters.
          </Text>
        </View>
      </View>
    )
  }

  const renderErrorUI = () => {
    return (
      <View style={{padding: 16}}>
        <Text style={[textStyles.body, {color: textMain}]}>
          {error}
        </Text>
      </View>
    )
  }

  const renderResultsUI = () => {
    return (
      <FlatList
        style={{flex: 1}}
        data={results}
        keyExtractor={(item) => item._id?.toString?.() || `${item.name}-${item.slug || "company"}`}
        contentContainerStyle={{
          width: "100%",
          maxWidth: MAX_CONTENT_WIDTH,
          alignSelf: "center",
          paddingHorizontal: 16,
          gap: 10,
          paddingVertical: 16
        }}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item: company, index }) => (
          <ShadowView>
            <TouchableOpacity
              style={{
                borderRadius: 12,
                padding: 12,
                backgroundColor: bgSecond,
                flexDirection: "row",
                gap: 12,
                alignItems: "center"
              }}
              onPress={() => {
                Haptics.selection()
                Analytics.track("company_clicked", {
                  companyId: company._id?.toString() || "",
                  companyName: company.name,
                  index,
                  source: "company-search-results",
                })
                navigation.navigate("Company", {
                  id: company._id,
                  name: company.name,
                  source: "company-search-results",
                  searchTerm: params.term
                })
              }}
            >
              <View style={{width: 44, height: 44, borderRadius: 8, overflow: "hidden", backgroundColor: `${borderColor}40`, justifyContent: "center", alignItems: "center"}}>
                <Logo
                  company={company}
                  style={{width: 44, height: 44}}
                />
              </View>
              <View style={{flex: 1, gap: 4}}>
                <AutoShrinkText
                  text={company.name}
                  textStyle={[textStyles.h4, {color: textMain}]}
                  containerStyle={{width: "100%"}}
                />
                {renderValueBadges(company)}
                <Text style={[textStyles.subscript, {color: textMain}]}>
                  {company.segment || "No segment"}
                </Text>
              </View>
            </TouchableOpacity>
          </ShadowView>
        )}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        onEndReachedThreshold={0.35}
        onEndReached={loadMore}
        ListFooterComponent={loadingMore ? (
          <View style={{paddingVertical: 16, alignItems: "center", justifyContent: "center"}}>
            <ActivityIndicator size="small" color={primary} />
          </View>
        ) : null}
      />
    )
  }

  return (
    <View style={{flex: 1, backgroundColor: bgMain}}>
      {bubbles.length > 0 && (
        <View style={{ paddingTop: 6, paddingBottom: 4}}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{gap: 6, paddingHorizontal: 16}}
          >
            {bubbles.map((bubble, index) => (
              <TouchableOpacity
                key={`${bubble.label}-${index}`}
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  borderRadius: 100,
                  paddingHorizontal: 8,
                  paddingVertical: 5,
                  backgroundColor: bubble.bgColor || `${primary}40`
                }}
              disabled={!bubble.onClear}
              onPress={bubble.onClear}
              >
                <Text style={[textStyles.label, {color: textMain}]}>
                {bubble.label}{bubble.onClear ? " ×" : ""}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      )}

      {loading && renderSkeletonUI()}
      {!loading && !!error && renderErrorUI()}
      {!loading && !error && results.length === 0 && renderNoResultsUI()}
      {!loading && !error && results.length > 0 && renderResultsUI()}
    </View>
  )
}

export default CompanySearchResults
